import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faNewspaper, faChartPie, faUsers, faLink, faCode, faListAlt, faMailBulk, faTags, faChartBar, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function PublishersNewsletterPage() {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    return (
        <>
            <Helmet prioritizeSeoTags>
                <meta charSet="utf-8" />
                <title>Engage Your Audience with Custom Newsletters | Glitch Gaming</title>
                <meta name="description" content="Craft vibrant custom newsletters with Glitch's built-in builder. Integrate seamlessly with your gaming community and keep players informed with analytics." />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="Engage Your Audience with Custom Newsletters | Glitch" />
                <meta property="og:description" content="Craft vibrant custom newsletters with Glitch's built-in builder. Integrate seamlessly with your gaming community and keep players informed with analytics." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/newsletter_banner.webp" />
            </Helmet>
            <Header />
            
            {/* Hero Section */}
            <section className="newsletter-hero bg-gradient">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 text-center text-lg-start py-5">
                            <h1 className="display-4 fw-bold mb-4 text-white">
                                Boost Engagement with<br />
                                <span className="text-gradient">Game-Changing Newsletters</span>
                            </h1>
                            <p className="lead mb-5 text-white-75">Directly connect with players through beautiful, data-driven email campaigns that keep your community engaged and informed.</p>
                            <div className="cta-group">
                                <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-danger btn-lg me-3 mb-3">
                                    Get Started  <FontAwesomeIcon icon={faChartPie} className="ms-2" />
                                </Link>
                                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg mb-3">
                                    Book A Demo
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="hero-visual mt-5 mt-lg-0">
                                <img
                                    src="/assets/images/publishers/newsletter_builder.png"
                                    alt="Newsletter Builder Preview"
                                    className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                                />
                                <div className="highlight-card analytics">
                                    <FontAwesomeIcon icon={faChartBar} className="me-2" />
                                    32% Average Open Rate
                                </div>
                                <div className="highlight-card template">
                                    <FontAwesomeIcon icon={faNewspaper} className="me-2" />
                                    Build Design Templates
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section py-5 bg-dark">
                <div className="container">
                    <h2 className="display-6 fw-bold mb-4 text-center">Benefits Of A Newsletter</h2>
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="stat-card bg-primary text-center p-4 rounded-3 h-100">
                                <FontAwesomeIcon icon={faEnvelope} className="fa-3x mb-3" />
                                <h3 className="text-white">32% Open Rate</h3>
                                <p className="text-white-75 mb-0">Industry-leading engagement for game newsletters</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="stat-card bg-success text-center p-4 rounded-3 h-100">
                                <FontAwesomeIcon icon={faChartBar} className="fa-3x mb-3" />
                                <h3 className="text-white">3.75% CTR</h3>
                                <p className="text-white-75 mb-0">Higher than average click-through rates</p>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="stat-card bg-danger text-center p-4 rounded-3 h-100">
                                <FontAwesomeIcon icon={faDollarSign} className="fa-3x mb-3" />
                                <h3 className="text-white">$42 ROI</h3>
                                <p className="text-white-75 mb-0">For every $1 spent on email marketing</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section py-5">
                <div className="container">
                    {/* Feature 1 */}
                    <div className="row align-items-center mb-5 g-5">
                        <div className="col-lg-6">
                            <div className="feature-visual border border-3 border-white rounded-3 overflow-hidden shadow-lg">
                                <img 
                                    src="/assets/images/publishers/newsletter_builder.png" 
                                    alt="Newsletter Builder" 
                                    className="img-fluid" 
                                />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="feature-content text-black">
                                <h2 className="display-6 fw-bold mb-4 text-black">Drag & Drop Newsletter Builder</h2>
                                <p className="lead mb-4">Create stunning, brand-consistent emails without coding. Choose from our library of gaming-focused templates or build your own.</p>
                                <ul className="feature-list list-unstyled">
                                    <li className="mb-3">
                                        <FontAwesomeIcon icon={faNewspaper} className="text-danger me-2" />
                                        Pre-designed gaming templates
                                    </li>
                                    <li className="mb-3">
                                        <FontAwesomeIcon icon={faCode} className="text-danger me-2" />
                                        Custom HTML/CSS support
                                    </li>
                                    <li className="mb-3">
                                        <FontAwesomeIcon icon={faUsers} className="text-danger me-2" />
                                        Team collaboration features
                                    </li>
                                </ul>
                                <div className="mt-4">
                                    <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-danger btn-lg me-3">
                                        Try Builder Now
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Feature 2 */}
                    <div className="row align-items-center mb-5 g-5 bg-dark text-white py-5 px-3 rounded-3 mt-5">
                        <div className="col-lg-6 order-lg-1">
                            <div className="feature-content">
                                <h2 className="display-6 fw-bold mb-4">Smart Audience Segmentation</h2>
                                <p className="lead mb-4">Target specific player groups with personalized content based on their in-game behavior and preferences.</p>
                                <ul className="feature-list list-unstyled">
                                    <li className="mb-3">
                                        <FontAwesomeIcon icon={faTags} className="text-primary me-2" />
                                        Dynamic player categories
                                    </li>
                                    <li className="mb-3">
                                        <FontAwesomeIcon icon={faChartPie} className="text-primary me-2" />
                                        Behavioral targeting
                                    </li>
                                    <li className="mb-3">
                                        <FontAwesomeIcon icon={faMailBulk} className="text-primary me-2" />
                                        Automated list management
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-lg-6 order-lg-0">
                            <div className="feature-visual border border-3 border-white rounded-3 overflow-hidden shadow-lg">
                                <img 
                                    src="/assets/images/publishers/newsletter_community.jpg" 
                                    alt="Audience Segmentation" 
                                    className="img-fluid" 
                                />
                            </div>
                        </div>
                    </div>

                    {/* Feature 3 */}
                    <div className="row align-items-center mb-5 g-5">
                        <div className="col-lg-6">
                            <div className="feature-visual border border-3 border-white rounded-3 overflow-hidden shadow-lg">
                                <img 
                                    src="/assets/images/publishers/web_analytics.png" 
                                    alt="Analytics Dashboard" 
                                    className="img-fluid" 
                                />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="feature-content text-black">
                                <h2 className="display-6 fw-bold mb-4 text-black">Real-Time Performance Analytics</h2>
                                <p className="lead mb-4">Track every aspect of your email campaigns with detailed metrics and conversion tracking.</p>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="metric-card bg-light p-3 mb-3 rounded-3">
                                            <h3 className="text-danger">98%+</h3>
                                            <p className="mb-0">Delivery Rate</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="metric-card bg-light p-3 mb-3 rounded-3">
                                            <h3 className="text-danger">3.75%</h3>
                                            <p className="mb-0">Average CTR</p>
                                        </div>
                                    </div>
                                </div>
                                <ul className="feature-list list-unstyled">
                                    <li className="mb-3">
                                        <FontAwesomeIcon icon={faChartBar} className="text-danger me-2" />
                                        Campaign comparison tools
                                    </li>
                                    <li className="mb-3">
                                        <FontAwesomeIcon icon={faLink} className="text-danger me-2" />
                                        Conversion tracking
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="newsletter-cta bg-gradient-reverse py-5">
                <div className="container text-center">
                    <h2 className="display-5 fw-bold mb-4 text-white">Start Engaging Your Players Today</h2>
                    <p className="cta-subtitle mb-5 text-white-75">Join hundreds of game developers boosting retention with Glitch Newsletters</p>
                    <div className="cta-wrapper">
                        <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-danger btn-lg px-5 py-3 me-3 mb-3">
                            Get Started
                        </Link>
                        <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg px-5 py-3 mb-3">
                            Schedule Demo
                        </Link>
                    </div>
                    <div className="trust-badges mt-5">
                        <div className="badge-item d-inline-flex align-items-center bg-white bg-opacity-10 px-4 py-2 rounded-pill me-3">
                            <FontAwesomeIcon icon={faChartPie} className="me-2" />
                            <span>Real-time Analytics</span>
                        </div>
                        <div className="badge-item d-inline-flex align-items-center bg-white bg-opacity-10 px-4 py-2 rounded-pill">
                            <FontAwesomeIcon icon={faUsers} className="me-2" />
                            <span>Player Segmentation</span>
                        </div>
                    </div>
                </div>
            </section>

            <IndieSubmenu />
            <Footer />

            <style jsx>{`
                .newsletter-hero {
                    background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
                    padding: 100px 0;
                }

                .text-gradient {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .hero-visual {
                    position: relative;
                    transform: perspective(1000px) rotateY(-5deg);
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                }

                .highlight-card {
                    position: absolute;
                    padding: 1rem 1.5rem;
                    border-radius: 12px;
                    backdrop-filter: blur(10px);
                    background: rgba(255,255,255,0.1);
                    border: 1px solid rgba(255,255,255,0.2);
                    max-width: 250px;
                    animation: float 6s ease-in-out infinite;
                }

                .highlight-card.analytics {
                    bottom: -20px;
                    left: -30px;
                }

                .highlight-card.template {
                    top: -20px;
                    right: -30px;
                }

                @keyframes float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-20px); }
                }

                .stats-section {
                    padding: 80px 0;
                }

                .stat-card {
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-10px);
                }

                .features-section {
                    padding: 80px 0;
                    background: #f8f9ff;
                }

                .feature-visual {
                    transition: transform 0.3s ease;
                }

                .feature-visual:hover {
                    transform: scale(1.02);
                }

                .newsletter-cta {
                    background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
                    padding: 100px 0;
                }

                .metric-card {
                    transition: transform 0.3s ease;
                }

                .metric-card:hover {
                    transform: translateY(-5px);
                }

                @media (max-width: 768px) {
                    .hero-visual {
                        transform: none;
                    }
                    
                    .highlight-card {
                        position: relative;
                        margin: 1rem auto;
                        max-width: 100%;
                    }
                }
            `}</style>
        </>
    );
}

export default PublishersNewsletterPage;